import {TemplateNode} from "#template/template-node/template-node"
import type {ExportNodeParameters} from "#template/fundamental/export/types"
import type {EvaluatedValueOf, InfoClassCtor} from "#template/fundamental/factory"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {OnCompileContext, OnFilterActiveContext, VisitorNodeResult, TemplateNodeContext} from "#template/template-node/types"
import type {Evaluator} from "@cm/graph-v3"
import type {ObjectPathPart} from "#template/interactions"
import {outputKeyFromOutputTypeAndOutputId} from "#template/interface-descriptors"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import {evaluateMetaData} from "#template/nodes/meta-data"
import {skipped} from "#template/template-node/constants"
import {visitNone} from "#template/template-node/visit-helpers"

const TD = TypeDescriptors

const objectPathPartArrayDesc = TD.Array(TD.ObjectPathPart)

/*Export logic:
- Every export node of a template creates an output descriptor to publish which ids provide outputs.
- It stores the evaluated result in the templateOutputs of the current context as `output-${outputType}-${id}`.
- In CompileTemplate the templateOutputs is made available via output, so that it becomes available in RunTemplate as outputs outlet.
- In template instance the outputs of RunTemplate is made available to the current template via an alias to `templateOutputs-${localId}`.
- It can then be consumed with an output node, that during evaluation will reference the corresponding output coming from `templateOutputs-${localId}` using `output-${outputType}-${id}`.*/

export abstract class ExportNode<out ItemValue = unknown> extends TemplateNode<ExportNodeParameters<ItemValue>> {
    abstract get ancestry(): readonly string[]
    abstract get InfoClass(): InfoClassCtor

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<ExportNodeParameters<ItemValue>> {
        if (this.parameters.node === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ExportNodeParameters<ItemValue>> {
        const {id, name: nodeName, node, metaData} = this.parameters
        const {evaluator, currentTemplate, path} = context
        const {descriptorList, templateOutputs} = currentTemplate
        const scope = evaluator.getScope(this)

        const evalValue = evaluator.evaluate(node as ItemValue | null) as BuilderInlet<EvaluatedValueOf<ItemValue> | null>
        const evaluatedMetaData = evaluateMetaData(scope, evaluator, metaData, id)

        const affectedObjectPathParts = scope.value<ObjectPathPart[]>(
            [
                {
                    path,
                    part: "root",
                },
            ],
            objectPathPartArrayDesc,
            "affectedObjectPathParts",
        )

        const InfoClass = this.InfoClass
        const ancestry = this.ancestry

        // Fan-out: write to every ancestor's namespace so e.g. a MeshExport is consumable by an
        // ObjectOutput in a parent template (Mesh value flows through as ObjectData).
        for (const ancestor of ancestry) {
            templateOutputs[outputKeyFromOutputTypeAndOutputId(ancestor, id)] = evalValue
        }
        descriptorList.push(
            scope.pureLambda(
                scope.tuple(id, nodeName, evalValue, this, evaluatedMetaData, affectedObjectPathParts, InfoClass),
                ([id, name, value, origin, metaData, affectedObjectPathParts, InfoClass]) =>
                    new InfoClass({id, name, value, type: "output" as const, origin, metaData, affectedObjectPathParts}),
                `${ancestry[0]}Info`,
            ),
        )

        return visitNone(this.parameters)
    }
}
